import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { createPortal } from 'react-dom';
import type { VoucherCardStyle, VoucherPrintLayout } from '@/api/vendo';

/**
 * The printed voucher card (Ray's approved preview, 2026-10-14) — three
 * styles, price/FREE and duration centred, and ONE size knob (`scale`) that
 * follows "cards per A4" so the on-screen preview is the printed size.
 *
 *   classic — white ticket: "WIFI VOUCHER" label, serif price, punched notches
 *   strip   — horizontal: price block left, store + code right; shortest card
 *   bold    — black card, yellow store band on top, huge yellow price
 *
 * Used twice: the live preview inside the Generate wizard, and the A4 sheet
 * that goes to the printer (VoucherPrintHost).
 */

export interface VoucherCardData {
   code: string;
   /** ₱ amount. Ignored when `free`. */
   price: number;
   /** Minutes of internet. */
   duration: number;
   free?: boolean;
   /** "valid 30 days" line; '' hides it. */
   validityText?: string;
}

/** Card SIZE, stored as the old "cards per A4" key (8 | 12 | 20 | 30) so
 *  saved batches and the server keep working: the key now means how many
 *  cards go ACROSS; the page then fits as many rows as it can. */
export const CARDS_PER_A4_OPTIONS: { value: 8 | 12 | 20 | 30; label: string; cols: number }[] = [
   { value: 8, label: 'Big — 2 across', cols: 2 },
   { value: 12, label: 'Medium — 3 across', cols: 3 },
   { value: 20, label: 'Small — 4 across', cols: 4 },
   { value: 30, label: 'Tiny — 5 across', cols: 5 },
];

/** A4 printable area (5 mm page margin) and the gap between cards, in mm. */
const PAGE_W = 200;
const PAGE_H = 287;
const GAP = 3;
/** Each style's card at scale 1, in mm (240 CSS px wide = 63.5 mm). Only a
 *  FALLBACK: the app's own page styles (line-height 1.5) make the real card
 *  taller, so the print measures the real card first (CardProbe). */
const CARD_W = 63.5;
const CARD_H: Record<VoucherCardStyle, number> = { strip: 31, classic: 42.5, bold: 42 };

/**
 * The A4 grid for a card size + style (Ray 2026-10-18: "maximize the paper"):
 * the size fixes the columns, then as many rows as fit are packed down the
 * page — no empty bands. Cards stretch a hair to fill their cell exactly.
 */
export function a4Grid(size: number, style: VoucherCardStyle, measuredHmm?: number) {
   const cols = (CARDS_PER_A4_OPTIONS.find((o) => o.value === size) ?? CARDS_PER_A4_OPTIONS[1]).cols;
   const wMm = (PAGE_W - GAP * (cols - 1)) / cols;
   const scale = (wMm / CARD_W) * 0.96;
   // A measured card (at this exact width) wins; +0.5 mm so a border never
   // lands on the cell edge (Ray 2026-10-18: bottom borders were cut).
   const cardH = measuredHmm ? measuredHmm + 0.5 : (CARD_H[style] ?? CARD_H.classic) * scale;
   const rows = Math.max(1, Math.floor((PAGE_H + GAP) / (cardH + GAP)));
   const hMm = (PAGE_H - GAP * (rows - 1)) / rows;
   return { cols, rows, wMm, hMm, scale, perSheet: cols * rows };
}

/** Preview scale per sheet density — the same numbers as the approved preview. */
export const PREVIEW_SCALE: Record<number, number> = { 8: 1.15, 12: 1, 20: 0.85, 30: 0.72 };

export function sheetsFor(count: number, size: number, style: VoucherCardStyle, measuredHmm?: number): number {
   return Math.max(1, Math.ceil(count / a4Grid(size, style, measuredHmm).perSheet));
}

/**
 * Measures the REAL printed card height, per card size, on this page with its
 * real styles — off-screen, never printed. Reports { size: mm }. Without a
 * layout engine (tests) nothing is reported and a4Grid keeps its fallback.
 */
export function CardProbe({
   card,
   layout,
   onMeasure,
}: {
   card: VoucherCardData;
   layout: Pick<VoucherPrintLayout, 'storeName' | 'cardStyle' | 'logoDataUrl'>;
   onMeasure: (mmBySize: Record<number, number>) => void;
}) {
   const refs = useRef<Record<number, HTMLDivElement | null>>({});
   const last = useRef('');
   useLayoutEffect(() => {
      const out: Record<number, number> = {};
      for (const o of CARDS_PER_A4_OPTIONS) {
         const px = refs.current[o.value]?.getBoundingClientRect().height ?? 0;
         if (px > 0) out[o.value] = Math.round(((px * 25.4) / 96) * 100) / 100;
      }
      const key = JSON.stringify(out);
      if (key !== last.current && Object.keys(out).length) {
         last.current = key;
         onMeasure(out);
      }
   });
   return createPortal(
      <ProbeHost className="otc-print-probe" aria-hidden>
         {CARDS_PER_A4_OPTIONS.map((o) => {
            const g = a4Grid(o.value, layout.cardStyle);
            return (
               <ProbeCell key={o.value} ref={(el) => (refs.current[o.value] = el)} style={{ width: `${g.wMm}mm` }}>
                  <VoucherCard card={card} layout={layout} scale={g.scale} logoDataUrl={layout.logoDataUrl} probe />
               </ProbeCell>
            );
         })}
      </ProbeHost>,
      document.body
   );
}

/** Measured printed-card heights, for the wizard's "per A4" numbers and the print. */
export function useMeasuredCardHeights() {
   const [mm, setMm] = useState<Record<number, number>>({});
   return [mm, setMm] as const;
}

/** "30 minutes" / "3 hours" / "1 day" — how the card says it. */
export function durationWords(mins: number): string {
   if (!Number.isFinite(mins) || mins <= 0) return '';
   if (mins % 1440 === 0) { const d = mins / 1440; return `${d} day${d === 1 ? '' : 's'}`; }
   if (mins % 60 === 0) { const h = mins / 60; return `${h} hour${h === 1 ? '' : 's'}`; }
   if (mins > 60) { const h = Math.floor(mins / 60); return `${h}h ${mins % 60}m`; }
   return `${mins} minute${mins === 1 ? '' : 's'}`;
}

/** Codes print with a dash after the prefix so they read in two halves. */
export function displayCode(code: string, prefix?: string): string {
   const p = (prefix || '').toUpperCase();
   if (p && code.toUpperCase().startsWith(p) && code.length > p.length) {
      return `${p}-${code.slice(p.length)}`;
   }
   return code;
}

export function VoucherCard({
   card,
   layout,
   scale = 1,
   logoDataUrl,
   className,
   probe = false,
}: {
   card: VoucherCardData;
   layout: Pick<VoucherPrintLayout, 'storeName' | 'cardStyle'>;
   scale?: number;
   logoDataUrl?: string;
   className?: string;
   /** A hidden measuring copy (CardProbe): no test ids. */
   probe?: boolean;
}) {
   const style = layout.cardStyle;
   return (
      <Card
         className={className}
         $style={style}
         style={{ ['--s' as string]: scale }}
         data-testid={probe ? undefined : 'voucher-card'}
         data-style={style}
      >
         <Head $style={style} className="vc-head">
            <Logo $style={style} className="vc-logo">
               {logoDataUrl ? <img src={logoDataUrl} alt="" /> : '🏪'}
            </Logo>
            <span className="vc-store">{layout.storeName || 'Your store'}</span>
         </Head>
         <Main $style={style} className="vc-main">
            <Price $style={style} className="vc-price" data-testid={probe ? undefined : 'voucher-card-price'}>
               {card.free ? 'FREE' : `₱${card.price}`}
            </Price>
            <Dur $style={style} className="vc-dur">{durationWords(card.duration)} WiFi</Dur>
         </Main>
         <Code $style={style} className="vc-code">{card.code}</Code>
         <Foot $style={style} className="vc-foot">
            Connect to WiFi · type code{card.validityText ? ` · ${card.validityText}` : ''}
         </Foot>
      </Card>
   );
}

/* ── the A4 sheet that goes to the printer ─────────────────────────── */

/**
 * Off-screen print host. Mounted only while printing; the print stylesheet
 * hides everything else on the page and lets this flow across A4 pages.
 * Cards are laid out on the a4Grid for their size + style, filling the
 * 200 × 287 mm printable area (5 mm page margin).
 */
export function VoucherPrintHost({
   cards,
   layout,
}: {
   cards: VoucherCardData[];
   layout: VoucherPrintLayout;
}) {
   const [measured, setMeasured] = useMeasuredCardHeights();
   const opt = a4Grid(layout.cardsPerA4, layout.cardStyle, measured[layout.cardsPerA4]);
   const gapMm = GAP;
   const { wMm, hMm, scale } = opt;
   const perSheet = opt.cols * opt.rows;
   const sheets: VoucherCardData[][] = [];
   for (let i = 0; i < cards.length; i += perSheet) sheets.push(cards.slice(i, i + perSheet));

   return createPortal(
      <>
         <OffscreenHost className="otc-print-offscreen" aria-hidden>
            {sheets.map((sheet, i) => (
               <Sheet key={i} className="otc-print-sheet" $cols={opt.cols} $rows={opt.rows} $gap={gapMm}>
                  {sheet.map((c) => (
                     <Cell key={c.code} $w={wMm} $h={hMm} $spread={layout.cardStyle !== 'strip'}>
                        <VoucherCard card={c} layout={layout} scale={scale} logoDataUrl={layout.logoDataUrl} />
                     </Cell>
                  ))}
               </Sheet>
            ))}
         </OffscreenHost>
         {cards[0] && <CardProbe card={cards[0]} layout={layout} onMeasure={setMeasured} />}
         <PrintGlobal />
      </>,
      document.body
   );
}

/* ── styles ────────────────────────────────────────────────────────── */

const s = (px: number) => `calc(${px}px * var(--s))`;

const Card = styled.div<{ $style: VoucherCardStyle }>`
   --s: 1;
   box-sizing: border-box;
   width: ${s(240)};
   border-radius: 8px;
   padding: ${s(10)} ${s(12)};
   display: grid;
   gap: ${s(3)};
   color: #1c2130;
   background: #fff;
   text-align: center;
   justify-items: center;
   font-size: ${s(12)};
   font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
   page-break-inside: avoid;
   break-inside: avoid;
   -webkit-print-color-adjust: exact;
   print-color-adjust: exact;
   ${({ $style }) =>
      $style === 'classic' &&
      css`
         border: 2px solid #1c2130;
         position: relative;
         &::before,
         &::after {
            content: '';
            position: absolute;
            top: 50%;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #fff;
            border: 2px solid #1c2130;
            transform: translateY(-50%);
         }
         &::before { left: -9px; clip-path: inset(0 0 0 50%); }
         &::after { right: -9px; clip-path: inset(0 50% 0 0); }
      `}
   ${({ $style }) =>
      $style === 'strip' &&
      css`
         grid-template-columns: auto 1fr;
         grid-template-areas: 'price head' 'price code' 'price foot';
         gap: 2px ${s(10)};
         text-align: left;
         justify-items: start;
         align-items: center;
         border: 1px solid #cfd3dd;
         border-left: ${s(6)} solid #4f63f6;
      `}
   ${({ $style }) =>
      $style === 'bold' &&
      css`
         background: #111318;
         color: #fff;
         border: 0;
         padding-top: 0;
         border-radius: 6px;
         overflow: hidden;
      `}
`;

const Head = styled.div<{ $style: VoucherCardStyle }>`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   font-weight: 700;
   letter-spacing: 0.03em;
   font-size: ${s(11)};
   ${({ $style }) =>
      $style === 'classic' &&
      css`
         flex-wrap: wrap;
         &::before {
            content: 'WIFI VOUCHER';
            display: block;
            width: 100%;
            font-size: ${s(9)};
            letter-spacing: 0.2em;
            opacity: 0.6;
            margin-bottom: 2px;
         }
      `}
   ${({ $style }) => $style === 'strip' && css`grid-area: head; justify-content: flex-start;`}
   ${({ $style }) =>
      $style === 'bold' &&
      css`
         background: #ffd60a;
         color: #111318;
         width: calc(100% + ${s(24)});
         margin: 0 calc(${s(12)} * -1);
         padding: ${s(6)} 0;
         text-transform: uppercase;
         letter-spacing: 0.12em;
         font-size: ${s(10)};
      `}
`;

const Logo = styled.span<{ $style: VoucherCardStyle }>`
   width: ${s(20)};
   height: ${s(20)};
   border-radius: 4px;
   display: inline-grid;
   place-items: center;
   background: ${({ $style }) => ($style === 'bold' ? '#111318' : 'rgba(0,0,0,.08)')};
   color: ${({ $style }) => ($style === 'bold' ? '#ffd60a' : 'inherit')};
   overflow: hidden;
   font-size: ${s(12)};
   img { width: 100%; height: 100%; object-fit: cover; }
`;

const Main = styled.div<{ $style: VoucherCardStyle }>`
   display: grid;
   justify-items: center;
   gap: 0;
   ${({ $style }) =>
      $style === 'strip' &&
      css`
         grid-area: price;
         padding-right: ${s(10)};
         border-right: 1px dashed #cfd3dd;
         align-self: stretch;
         align-content: center;
      `}
   ${({ $style }) => $style === 'bold' && css`padding: ${s(6)} 0 0;`}
`;

const Price = styled.div<{ $style: VoucherCardStyle }>`
   font-size: ${s(30)};
   font-weight: 800;
   line-height: 1.05;
   ${({ $style }) => $style === 'classic' && css`font-family: Georgia, 'Times New Roman', serif; font-weight: 700;`}
   ${({ $style }) => $style === 'strip' && css`font-size: ${s(24)};`}
   ${({ $style }) => $style === 'bold' && css`font-size: ${s(40)}; color: #ffd60a; letter-spacing: -0.02em;`}
`;

const Dur = styled.div<{ $style: VoucherCardStyle }>`
   font-size: ${s(12)};
   opacity: 0.85;
   ${({ $style }) =>
      $style === 'bold' && css`text-transform: uppercase; letter-spacing: 0.1em; font-size: ${s(10)}; opacity: 0.9;`}
`;

const Code = styled.div<{ $style: VoucherCardStyle }>`
   font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
   font-weight: 800;
   font-size: ${s(18)};
   letter-spacing: 0.12em;
   padding: ${s(5)} ${s(10)};
   border-radius: 5px;
   background: rgba(0, 0, 0, 0.06);
   width: 100%;
   box-sizing: border-box;
   ${({ $style }) => $style === 'strip' && css`grid-area: code; font-size: ${s(16)}; width: auto;`}
   ${({ $style }) =>
      $style === 'bold' && css`background: transparent; border: 2px solid #fff; color: #fff; border-radius: 3px;`}
`;

const Foot = styled.div<{ $style: VoucherCardStyle }>`
   font-size: ${s(9.5)};
   opacity: 0.7;
   ${({ $style }) => $style === 'strip' && css`grid-area: foot;`}
   ${({ $style }) => $style === 'bold' && css`opacity: 0.6;`}
`;

const ProbeHost = styled.div`
   position: fixed;
   left: -20000px;
   top: 0;
   visibility: hidden;
   pointer-events: none;
`;
/* Same rule as Cell: the card takes the full cell width, height is its own. */
const ProbeCell = styled.div`
   > * { width: 100% !important; }
`;

const OffscreenHost = styled.div`
   position: fixed;
   left: -10000px;
   top: 0;
   width: 210mm;
   pointer-events: none;
`;

const Sheet = styled.div<{ $cols: number; $rows: number; $gap: number }>`
   display: grid;
   grid-template-columns: repeat(${({ $cols }) => $cols}, max-content);
   gap: ${({ $gap }) => $gap}mm;
   justify-content: start;
   align-content: start;
   width: 200mm;
   height: 287mm;
   box-sizing: border-box;
   page-break-after: always;
   break-after: page;
   background: #fff;
`;

/* The card FILLS its cell (Ray 2026-10-18: "the A4 is not maximized" — a
   card sized by width alone left half of each 3×4 cell empty). Content stays
   centred inside; borders and fill reach the cell edges. */
const Cell = styled.div<{ $w: number; $h: number; $spread: boolean }>`
   width: ${({ $w }) => $w}mm;
   height: ${({ $h }) => $h}mm;
   display: grid;
   overflow: hidden;
   > * {
      width: 100% !important;
      height: 100%;
      /* Stacked cards (classic, bold) keep their top band at the top and the
         footer at the bottom; the side-by-side strip stays centred. */
      align-content: ${({ $spread }) => ($spread ? 'space-between' : 'center')};
   }
`;

// Same approach as the device-settings bulk print: hide every child of body
// at print time, reveal the host, keep it in normal flow so the sheets
// paginate.
const PrintGlobal = createGlobalStyle`
   @media print {
      @page { size: A4; margin: 5mm; }
      html, body {
         margin: 0 !important;
         padding: 0 !important;
         background: #fff !important;
         height: auto !important;
         overflow: visible !important;
      }
      body > * { display: none !important; }
      body > .otc-print-offscreen {
         display: block !important;
         position: static !important;
         left: auto !important;
         top: auto !important;
         width: auto !important;
         pointer-events: auto !important;
      }
      .otc-print-sheet:last-child { page-break-after: auto; break-after: auto; }
      .otc-print-probe { display: none !important; }
      .otc-print-offscreen * {
         -webkit-print-color-adjust: exact !important;
         print-color-adjust: exact !important;
      }
   }
`;
